/**
 * "Up Next" for maintenance staff: the work they should start on, in the order to start it.
 *
 * ONE rule for every kind of row, so a follow-up and its work order can never disagree:
 *   1. Urgent, then High, then everything else (Medium and Low together).
 *   2. Within a group, earliest due date first; no due date sorts after every dated item.
 *   3. On an exact tie, the viewer's own work beats unassigned work.
 * A follow-up carries its work order's priority and due date, so it sorts exactly like it.
 */
export type UpNextKind = 'work-order' | 'follow-up' | 'task';

export type UpNextItem = {
	key: string;
	kind: UpNextKind;
	priority: string | null | undefined;
	dueMs: number | null;
	mine: boolean;
	href: string;
	title: string;
	subtitle?: string;
	/** Plain-text start of the work order's description. Display only — never ranked on. */
	preview?: string;
};

const PRIORITY_GROUP: Record<string, number> = { urgent: 0, high: 1 };
const REST_GROUP = 2;

const groupOf = (priority: string | null | undefined): number =>
	PRIORITY_GROUP[(priority ?? '').toLowerCase()] ?? REST_GROUP;

/** Malformed runtime data (NaN, undefined, ±Infinity) counts as "no due date" so the order stays transitive. */
const dueOf = (ms: number | null): number | null => (Number.isFinite(ms) ? ms : null);

/** Comparator for {@link rankUpNext}; exported for callers that sort in place. */
export const compareUpNext = (a: UpNextItem, b: UpNextItem): number => {
	const group = groupOf(a.priority) - groupOf(b.priority);
	if (group !== 0) return group;
	const dueA = dueOf(a.dueMs);
	const dueB = dueOf(b.dueMs);
	if (dueA !== dueB) {
		if (dueA === null) return 1;
		if (dueB === null) return -1;
		return dueA < dueB ? -1 : 1;
	}
	if (a.mine !== b.mine) return a.mine ? -1 : 1;
	return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
};

/** Ranked copy (never mutates the input), optionally truncated to `limit`. */
export const rankUpNext = (items: readonly UpNextItem[], limit?: number): UpNextItem[] => {
	const ranked = [...items].sort(compareUpNext);
	return limit === undefined ? ranked : ranked.slice(0, Math.max(0, limit));
};
